import { useEffect } from 'react';

/**
 * Injects a single FAQPage JSON-LD `<script>` into <head> while the
 * mounting component is alive, then removes it on unmount.
 *
 * Google's FAQ rich-result policy requires that the question/answer
 * text injected via this schema match visible content on the page.
 * Always render the same Q&A in the DOM alongside this schema —
 * otherwise Google can flag the markup as spam and revoke rich
 * results.
 *
 * Usage:
 *   <FAQSchema faqs={[
 *     { question: 'How much filament can I save?', answer: 'Up to 60%...' },
 *   ]} />
 */
const FAQSchema = ({ faqs }) => {
  useEffect(() => {
    if (!Array.isArray(faqs) || faqs.length === 0) return;

    const payload = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer,
        },
      })),
    };

    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-pm-faq', '1');
    tag.textContent = JSON.stringify(payload);
    document.head.appendChild(tag);

    return () => {
      document.head.removeChild(tag);
    };
  }, [faqs]);

  return null;
};

export default FAQSchema;
